import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'antd-mobile';
import i18n from '../../../i18n';
import QRCode from 'qrcode';
import { whatsappApi, MyWhatsAppAccountBinding, WhatsAppAuthMode, WhatsAppConnectionState } from '../../../api/whatsapp';
import { ApiEnvelope } from '../../../api/types';
import { BindAccountFormValues, ProfileWhatsAppAccountsState } from './types';
import { confirmDialog } from '../../../components/ConfirmDialog';

interface RequestErrorShape {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

function readErrorMessage(error: RequestErrorShape, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

type ConnectStatus =
  | 'idle'
  | 'loading'
  | 'qr_pending'
  | 'pairing_code_pending'
  | 'connected'
  | 'error';

const CONNECTION_POLL_INTERVAL_MS = 2500;
const CONNECTION_POST_INIT_SYNC_RETRIES = 3;
const CONNECTION_POST_INIT_SYNC_DELAY_MS = 350;

function normalizePhoneNumber(input?: string | null): string {
  return String(input || '').replace(/[^\d]/g, '');
}

function isConnectionLoadingStatus(status: WhatsAppConnectionState['status']): boolean {
  return (
    status === 'initializing' ||
    status === 'initializing_browser' ||
    status === 'injecting_page' ||
    status === 'awaiting_qr_or_code' ||
    status === 'recovering'
  );
}

function shouldCancelPendingConnection(
  status: ConnectStatus,
  phase: WhatsAppConnectionState['status'] | null
): boolean {
  return (
    status === 'loading' ||
    status === 'qr_pending' ||
    status === 'pairing_code_pending' ||
    (phase ? isConnectionLoadingStatus(phase) : false)
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function useProfileWhatsAppAccountsModel() {
  const [state, setState] = useState<ProfileWhatsAppAccountsState>({
    loading: true,
    submitting: false,
  });
  const [bindings, setBindings] = useState<MyWhatsAppAccountBinding[]>([]);

  const [connectVisible, setConnectVisible] = useState(false);
  const [connectBindingId, setConnectBindingId] = useState<string | null>(null);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus>('idle');
  const [connectPhase, setConnectPhase] = useState<WhatsAppConnectionState['status'] | null>(null);
  const [connectAuthMode, setConnectAuthMode] = useState<WhatsAppAuthMode>('pairing_code');
  const [connectQrImage, setConnectQrImage] = useState<string | null>(null);
  const [connectPairingCode, setConnectPairingCode] = useState<string | null>(null);
  const [connectPairingCodeExpiresInMs, setConnectPairingCodeExpiresInMs] = useState<number | null>(null);
  const [connectErrorMessage, setConnectErrorMessage] = useState('');
  const [connectVerifying, setConnectVerifying] = useState(false);
  const connectVerifyOnceRef = useRef(false);
  const connectInitInFlightRef = useRef(false);
  const activeRequestIdRef = useRef<string | null>(null);
  const activeAttemptIdRef = useRef<string | null>(null);
  const pendingCloseRef = useRef<Promise<void> | null>(null);

  const connectBinding = useMemo(() => (
    connectBindingId
      ? bindings.find((binding) => binding.id === connectBindingId) || null
      : null
  ), [bindings, connectBindingId]);

  const canUsePairingCode = useMemo(() => (
    Boolean(normalizePhoneNumber(connectBinding?.account?.phone_number))
  ), [connectBinding?.account?.phone_number]);

  const applyConnectionState = useCallback(async (
    incoming: WhatsAppConnectionState,
    fallbackMode: WhatsAppAuthMode,
    options?: { allowRequestAdoption?: boolean }
  ): Promise<boolean> => {
    const allowRequestAdoption = options?.allowRequestAdoption === true;
    const incomingRequestId = incoming.request_id || null;
    const incomingAttemptId = incoming.attempt_id || null;

    if (activeRequestIdRef.current) {
      if (incomingRequestId && incomingRequestId !== activeRequestIdRef.current) {
        return false;
      }
    } else if (allowRequestAdoption && incomingRequestId) {
      activeRequestIdRef.current = incomingRequestId;
    }

    if (activeRequestIdRef.current && incomingRequestId === activeRequestIdRef.current && incomingAttemptId) {
      activeAttemptIdRef.current = incomingAttemptId;
    } else if (!activeRequestIdRef.current) {
      if (
        activeAttemptIdRef.current &&
        incomingAttemptId &&
        incomingAttemptId !== activeAttemptIdRef.current &&
        incoming.status !== 'ready' &&
        incoming.status !== 'connected' &&
        incoming.status !== 'disconnected' &&
        incoming.status !== 'not_initialized'
      ) {
        return false;
      }

      if (incomingAttemptId) {
        activeAttemptIdRef.current = incomingAttemptId;
      }
    }

    setConnectPhase(incoming.status);

    if (incoming.status === 'ready' || incoming.status === 'connected') {
      if (incomingRequestId) {
        activeRequestIdRef.current = incomingRequestId;
      }
      setConnectStatus('connected');
      setConnectQrImage(null);
      setConnectPairingCode(null);
      setConnectPairingCodeExpiresInMs(null);
      setConnectErrorMessage('');
      return true;
    }

    if (
      incoming.status === 'worker_unavailable' ||
      incoming.status === 'worker_starting' ||
      incoming.status === 'worker_dependency_missing' ||
      incoming.status === 'worker_config_invalid' ||
      incoming.status === 'worker_lock_conflict' ||
      incoming.status === 'worker_degraded' ||
      incoming.status === 'need_reauth' ||
      incoming.status === 'auth_failure' ||
      incoming.status === 'error' ||
      incoming.status === 'fatal_error'
    ) {
      if (incomingRequestId) {
        activeRequestIdRef.current = incomingRequestId;
      }
      setConnectStatus('error');
      setConnectQrImage(null);
      setConnectPairingCode(null);
      setConnectPairingCodeExpiresInMs(null);
      setConnectErrorMessage(incoming.message || i18n.t('profile:waAccounts.connectFailed'));
      return true;
    }

    if (isConnectionLoadingStatus(incoming.status)) {
      setConnectAuthMode(incoming.auth_mode || fallbackMode);
      setConnectStatus('loading');
      setConnectPairingCodeExpiresInMs(incoming.pairing_code_expires_in_ms);
      setConnectErrorMessage('');
      return true;
    }

    if (incoming.pairing_code) {
      if (incomingRequestId) {
        activeRequestIdRef.current = incomingRequestId;
      }
      setConnectAuthMode('pairing_code');
      setConnectPairingCode(incoming.pairing_code);
      setConnectPairingCodeExpiresInMs(incoming.pairing_code_expires_in_ms);
      setConnectQrImage(null);
      setConnectStatus('pairing_code_pending');
      setConnectErrorMessage('');
      return true;
    }

    if (incoming.qr) {
      if (incomingRequestId) {
        activeRequestIdRef.current = incomingRequestId;
      }
      const nextQrImage = await QRCode.toDataURL(incoming.qr, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 240,
      });
      setConnectAuthMode('qr');
      setConnectQrImage(nextQrImage);
      setConnectPairingCode(null);
      setConnectPairingCodeExpiresInMs(null);
      setConnectStatus('qr_pending');
      setConnectErrorMessage('');
      return true;
    }

    setConnectQrImage(null);
    setConnectPairingCode(null);
    setConnectPairingCodeExpiresInMs(null);
    setConnectErrorMessage('');
    setConnectStatus('idle');

    if (incoming.status === 'disconnected' || incoming.status === 'not_initialized') {
      activeRequestIdRef.current = null;
      activeAttemptIdRef.current = null;
      setConnectPhase(null);
      setConnectAuthMode(fallbackMode);
      connectVerifyOnceRef.current = false;
    }
    return true;
  }, []);

  const cancelActiveInitializationIfNeeded = useCallback(async (
    binding: MyWhatsAppAccountBinding,
    fallbackMode: WhatsAppAuthMode
  ) => {
    if (!shouldCancelPendingConnection(connectStatus, connectPhase)) {
      return;
    }

    try {
      await whatsappApi.cancelInitSession(binding.wa_account_id) as { data: ApiEnvelope<WhatsAppConnectionState> };
    } catch {
      // Ignore cancellation failures and let the next init request re-sync state.
    } finally {
      activeRequestIdRef.current = null;
      activeAttemptIdRef.current = null;
      setConnectPhase(null);
      setConnectAuthMode(fallbackMode);
    }
  }, [connectPhase, connectStatus]);

  const load = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const response = await whatsappApi.getMyAccounts() as { data: ApiEnvelope<MyWhatsAppAccountBinding[]> };
      setBindings(response.data.data || []);
      setState((previous) => ({ ...previous, loading: false }));
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.loadFailed')),
      });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const requestConnectionFor = useCallback(async (
    binding: MyWhatsAppAccountBinding,
    options?: { mode?: WhatsAppAuthMode }
  ) => {
    if (pendingCloseRef.current) {
      await pendingCloseRef.current;
    }
    if (connectInitInFlightRef.current) {
      return;
    }

    const phoneNumber = normalizePhoneNumber(binding.account.phone_number) || null;
    const preferredMode: WhatsAppAuthMode = options?.mode || (phoneNumber ? 'pairing_code' : 'qr');

    setConnectAuthMode(preferredMode);
    setConnectStatus('loading');
    setConnectPhase('initializing');
    setConnectQrImage(null);
    setConnectPairingCode(null);
    setConnectPairingCodeExpiresInMs(null);
    setConnectErrorMessage('');
    connectVerifyOnceRef.current = false;
    await cancelActiveInitializationIfNeeded(binding, preferredMode);
    activeRequestIdRef.current = null;
    activeAttemptIdRef.current = null;

    if (preferredMode === 'pairing_code' && !phoneNumber) {
      setConnectStatus('error');
      setConnectErrorMessage(i18n.t('profile:waAccounts.connectMissingPhone'));
      setConnectPhase(null);
      return;
    }

    connectInitInFlightRef.current = true;
    try {
      const response = await whatsappApi.initSession(binding.wa_account_id, {
        auth_mode: preferredMode,
        phone_number: preferredMode === 'pairing_code' ? (phoneNumber || undefined) : undefined,
      }) as { data: ApiEnvelope<WhatsAppConnectionState> };

      const initState = response.data?.data;
      if (initState) {
        if (initState.request_id) {
          activeRequestIdRef.current = initState.request_id;
        }
        await applyConnectionState(initState, preferredMode, { allowRequestAdoption: true });
      }

      for (let attempt = 0; attempt < CONNECTION_POST_INIT_SYNC_RETRIES; attempt += 1) {
        await wait(CONNECTION_POST_INIT_SYNC_DELAY_MS);
        const stateResponse = await whatsappApi.getConnectionState(binding.wa_account_id) as { data: ApiEnvelope<WhatsAppConnectionState> };
        const latest = stateResponse.data.data;
        await applyConnectionState(latest, preferredMode);
        if (latest.status === 'ready' || latest.status === 'connected' || latest.qr || latest.pairing_code) {
          break;
        }
      }
    } catch (error) {
      setConnectStatus('error');
      setConnectErrorMessage(readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.connectFailed')));
      setConnectPhase(null);
    } finally {
      connectInitInFlightRef.current = false;
    }
  }, [applyConnectionState]);

  const requestConnection = useCallback(async (options?: { mode?: WhatsAppAuthMode }) => {
    if (!connectBinding) {
      return;
    }
    await requestConnectionFor(connectBinding, options);
  }, [connectBinding, requestConnectionFor]);

  const openConnect = useCallback(async (bindingId: string) => {
    if (pendingCloseRef.current) {
      await pendingCloseRef.current;
    }
    setConnectBindingId(bindingId);
    setConnectVisible(true);
    setConnectStatus('loading');
    setConnectPhase('initializing');
    setConnectQrImage(null);
    setConnectPairingCode(null);
    setConnectPairingCodeExpiresInMs(null);
    setConnectErrorMessage('');
    setConnectVerifying(false);
    connectVerifyOnceRef.current = false;
    activeRequestIdRef.current = null;
    activeAttemptIdRef.current = null;

    const nextBinding = bindings.find((binding) => binding.id === bindingId) || null;
    if (!nextBinding) {
      setConnectStatus('error');
      setConnectPhase(null);
      setConnectErrorMessage(i18n.t('profile:waAccounts.loadFailed'));
      return;
    }

    const phoneNumber = normalizePhoneNumber(nextBinding.account.phone_number) || null;
    const preferredMode: WhatsAppAuthMode = phoneNumber ? 'pairing_code' : 'qr';
    setConnectAuthMode(preferredMode);

    try {
      const response = await whatsappApi.getConnectionState(nextBinding.wa_account_id) as { data: ApiEnvelope<WhatsAppConnectionState> };
      const current = response.data.data;
      await applyConnectionState(current, preferredMode, { allowRequestAdoption: true });
      if (current.status === 'ready' || current.status === 'connected' || current.qr || current.pairing_code) {
        return;
      }
      await requestConnectionFor(nextBinding, { mode: preferredMode });
    } catch (error) {
      setConnectStatus('error');
      setConnectErrorMessage(readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.connectFailed')));
      setConnectPhase(null);
    }
  }, [applyConnectionState, bindings, requestConnectionFor]);

  const closeConnect = useCallback(() => {
    const active = connectBinding;
    const shouldCancel = Boolean(active) && shouldCancelPendingConnection(connectStatus, connectPhase);
    const fallbackMode = connectAuthMode;
    setConnectVisible(false);
    setConnectBindingId(null);
    setConnectStatus('idle');
    setConnectPhase(null);
    setConnectQrImage(null);
    setConnectPairingCode(null);
    setConnectPairingCodeExpiresInMs(null);
    setConnectErrorMessage('');
    setConnectVerifying(false);
    connectVerifyOnceRef.current = false;
    connectInitInFlightRef.current = false;
    activeRequestIdRef.current = null;
    activeAttemptIdRef.current = null;

    if (shouldCancel && active) {
      const closePromise = (async () => {
        try {
          await whatsappApi.cancelInitSession(active.wa_account_id) as { data: ApiEnvelope<WhatsAppConnectionState> };
        } catch {
          // Ignore cancellation failures after the modal has been closed.
        } finally {
          activeRequestIdRef.current = null;
          activeAttemptIdRef.current = null;
          setConnectAuthMode(fallbackMode);
        }
      })();
      pendingCloseRef.current = closePromise;
      void closePromise.finally(() => {
        if (pendingCloseRef.current === closePromise) {
          pendingCloseRef.current = null;
        }
      });
    }
  }, [connectAuthMode, connectBinding, connectPhase, connectStatus]);

  const pollConnectionState = useCallback(async () => {
    if (!connectBinding || !connectVisible) {
      return;
    }

    const fallbackMode = connectAuthMode;
    const response = await whatsappApi.getConnectionState(connectBinding.wa_account_id) as { data: ApiEnvelope<WhatsAppConnectionState> };
    const nextState = response.data.data;
    await applyConnectionState(nextState, fallbackMode);

    if ((nextState.status === 'ready' || nextState.status === 'connected') && !connectVerifyOnceRef.current) {
      connectVerifyOnceRef.current = true;
      setConnectVerifying(true);
      try {
        await whatsappApi.verifyMyAccountBinding(connectBinding.id);
        Toast.show({ icon: 'success', content: i18n.t('profile:waAccounts.connectVerifiedToast') });
        await load();
        closeConnect();
      } catch (error) {
        setConnectStatus('error');
        setConnectErrorMessage(readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.connectVerifyFailed')));
        setConnectVerifying(false);
        connectVerifyOnceRef.current = false;
      }
    }
  }, [applyConnectionState, closeConnect, connectAuthMode, connectBinding, connectVisible, load]);

  useEffect(() => {
    if (!connectVisible || !connectBinding) {
      return;
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) {
        return;
      }
      try {
        await pollConnectionState();
      } catch {
        if (!cancelled) {
          setConnectStatus('error');
          setConnectErrorMessage(i18n.t('profile:waAccounts.connectFailed'));
        }
      }
      if (!cancelled && connectVisible) {
        window.setTimeout(tick, CONNECTION_POLL_INTERVAL_MS);
      }
    };

    window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
    };
  }, [connectBinding, connectVisible, pollConnectionState]);

  const bindNewPhone = useCallback(async (values: BindAccountFormValues) => {
    setState((previous) => ({ ...previous, submitting: true }));
    try {
      const response = await whatsappApi.bindMyAccount({
        phone_number: values.phone_number.trim(),
        alias: values.alias?.trim() || undefined,
        set_default: values.set_default,
      }) as { data: ApiEnvelope<MyWhatsAppAccountBinding> };
      Toast.show({ icon: 'success', content: i18n.t('profile:waAccounts.bindSuccess') });
      const created = response.data.data;
      setBindings((previous) => {
        const filtered = previous.filter((item) => item.id !== created.id);
        return [created, ...filtered];
      });
      await load();
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.bindFailed')),
      });
    } finally {
      setState((previous) => ({ ...previous, submitting: false }));
    }
  }, [load]);

  const setDefault = useCallback(async (bindingId: string) => {
    setState((previous) => ({ ...previous, submitting: true }));
    try {
      await whatsappApi.updateMyAccountBinding(bindingId, { is_default: true });
      Toast.show({ icon: 'success', content: i18n.t('profile:waAccounts.setDefaultSuccess') });
      await load();
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.setDefaultFailed')),
      });
    } finally {
      setState((previous) => ({ ...previous, submitting: false }));
    }
  }, [load]);

  const unbind = useCallback(async (bindingId: string) => {
    const confirmed = await confirmDialog({
      title: i18n.t('profile:waAccounts.unbindConfirmTitle'),
      content: i18n.t('profile:waAccounts.unbindConfirmContent'),
      confirmText: i18n.t('profile:waAccounts.unbindConfirmOk'),
      cancelText: i18n.t('profile:waAccounts.unbindConfirmCancel'),
      tone: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setState((previous) => ({ ...previous, submitting: true }));
    try {
      await whatsappApi.unbindMyAccount(bindingId);
      Toast.show({ icon: 'success', content: i18n.t('profile:waAccounts.unbindSuccess') });
      await load();
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: readErrorMessage(error as RequestErrorShape, i18n.t('profile:waAccounts.unbindFailed')),
      });
    } finally {
      setState((previous) => ({ ...previous, submitting: false }));
    }
  }, [load]);

  return {
    state,
    bindings,
    reload: load,
    bindNewPhone,
    setDefault,
    unbind,
    connect: {
      visible: connectVisible,
      binding: connectBinding,
      status: connectStatus,
      phase: connectPhase,
      authMode: connectAuthMode,
      qrImage: connectQrImage,
      pairingCode: connectPairingCode,
      pairingCodeExpiresInMs: connectPairingCodeExpiresInMs,
      errorMessage: connectErrorMessage,
      verifying: connectVerifying,
      canUsePairingCode,
    },
    openConnect,
    closeConnect,
    requestConnection,
  };
}
